<template>
  <Dialog v-model:visible="isOpen" @show="focusMainInput" modal closable dismissableMask :focusOnShow="false"
    :draggable="false" :style="{ width: '80vw', height: '80vh', maxHeight: '80vh' }" :pt="{
      root: { class: 'agent-chat-dialog border border-charcoal-200 rounded-3xl shadow-2xl overflow-hidden dark:border-charcoal-800' },
      mask: { class: 'p-4 sm:p-6' },
      header: { class: 'shrink-0 border-b border-charcoal-100 bg-white px-6 py-4 dark:border-charcoal-800 dark:bg-charcoal-900' },
      content: { class: 'p-0 flex-1 overflow-hidden bg-semiwhite dark:bg-charcoal-900' },
      headerActions: { class: 'ml-3' },
      pcCloseButton: { root: { class: 'hover:bg-iris-600 hover:text-white' } },
    }">
    <template #header>
      <div class="flex min-w-0 flex-1 items-center justify-between gap-3 pr-2">
        <div class="flex min-w-0 items-center gap-3">
          <div class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-iris-600 text-white">
            <Icon icon="tabler:robot" class="size-7" />
          </div>
          <div class="min-w-0">
            <div class="truncate text-lg font-attention font-semibold text-charcoal-900 dark:text-charcoal-100">AI
              Assistant</div>
            <p class="truncate text-sm text-charcoal-500 dark:text-charcoal-400">Ask questions, review results, and
              inspect tool calls inline.</p>
          </div>
        </div>

        <button type="button"
          class="flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full border border-charcoal-200 px-3 py-1.5 text-xs font-medium text-charcoal-600 transition-colors hover:bg-charcoal-100 dark:border-charcoal-700 dark:text-charcoal-300 dark:hover:bg-charcoal-800"
          @click="openToolsPopover">
          <Icon icon="heroicons:wrench-screwdriver" class="size-3.5" />
          <span>Capabilities</span>
        </button>
        <ToolsPopover ref="toolsPopover" @select="handleCapabilitySelect" />
      </div>
    </template>

    <div
      class="flex h-full min-h-0 flex-col bg-semiwhite text-charcoal transition-colors duration-300 dark:bg-charcoal-900 dark:text-charcoal-100">
      <div class="flex min-h-0 flex-1 overflow-hidden">
        <aside v-if="saveToHistory" id="agent-chat-history"
          class="flex h-full w-72 shrink-0 flex-col border-r border-charcoal-100 bg-charcoal-950 text-charcoal-200 opacity-100 transition-all duration-300 dark:border-charcoal-800 dark:bg-charcoal">
          <div class="flex shrink-0 items-center gap-2 border-b border-charcoal-800 p-4">
            <button
              class="flex flex-1 items-center justify-center gap-2 rounded-xl bg-iris-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-iris-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
              @click="handleStartNewConversation" :disabled="streaming">
              <Icon icon="heroicons:plus" class="size-4" />
              <span>New Conversation</span>
            </button>
          </div>

          <div class="agent-chat-scrollbar flex-1 overflow-y-auto px-3 pb-4 pt-3">
            <div class="px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-charcoal-500">History</div>

            <div v-if="historyLoading" class="px-3 py-4 text-sm italic text-charcoal-400">Loading conversations...</div>
            <div v-else-if="historyError" class="px-3 py-4 text-sm text-peach-500">{{ historyError }}</div>
            <div v-else-if="!sortedConversations.length" class="px-3 py-4 text-sm italic text-charcoal-400">No past
              conversations yet.</div>

            <div v-for="conversation in sortedConversations" :key="conversation.id"
              class="group mb-1 flex items-start gap-2 rounded-xl px-3 py-3 transition-colors"
              :class="activeConversationId === conversation.id ? 'bg-charcoal-800 text-white' : 'text-charcoal-300 hover:bg-charcoal-850 hover:text-charcoal-100'">
              <button class="min-w-0 flex-1 cursor-pointer text-left disabled:cursor-not-allowed" @click="handleSelectConversation(conversation)" :disabled="streaming">
                <div class="flex items-center gap-1.5">
                  <Icon v-if="isConversationStreaming(conversation)" icon="heroicons:arrow-path"
                    class="size-3.5 shrink-0 animate-spin text-iris-400" aria-hidden="true" />
                  <span class="truncate text-sm font-medium">{{ conversationTitle(conversation) }}</span>
                </div>
                <div class="text-[11px] uppercase tracking-[0.14em] text-charcoal-500">{{
                  isConversationStreaming(conversation) ? 'Working…' : formatConversationTime(conversation) }}</div>
              </button>
              <button
                class="mt-0.5 shrink-0 cursor-pointer rounded-lg p-1 text-charcoal-500 transition-colors hover:text-peach-500 disabled:cursor-not-allowed disabled:opacity-50"
                :class="activeConversationId === conversation.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'"
                :disabled="streaming || isDeletingConversation(conversation.id)"
                @click.stop="deleteConversation(conversation)"
                :aria-label="`Delete ${conversationTitle(conversation)}`">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                  aria-hidden="true">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
              </button>
            </div>
          </div>
        </aside>

        <div class="flex min-w-0 flex-1 flex-col">
          <div class="relative min-h-0 flex-1">
            <div ref="scroller" @scroll="handleScroll"
              class="agent-chat-scrollbar h-full overflow-y-auto bg-white/70 px-4 py-5 dark:bg-charcoal-900 sm:px-6 sm:py-6">
              <Conversation
                class="mx-auto max-w-4xl"
                :messages="messages"
                :streaming="streaming"
                :stream-buffer="streamBuffer"
                :active-tool="activeTool"
                :pending-question="pendingQuestion"
                :pending-boost="pendingBoost"
                :pending-tool-prompt="pendingToolPrompt"
                :pending-task="pendingTask"
                :pending-field-lens-request="pendingFieldLensRequest"
                :suggested-prompts="suggestedPrompts"
                @answer="handleAnswer"
                @suggestion="handleSuggestedPrompt"
                @retry="handleRetry"
                @regenerate="handleRegenerate"
                @edit="handleEdit"
                @close-task="closeTaskComposer"
                @submit-task="handleTaskSubmit"
                @close-field-lens-request="closeFieldLensRequestComposer"
                @submit-field-lens-request="handleFieldLensRequestSubmit" />
            </div>

            <button v-if="!isNearBottom" @click="scrollBottom(true)"
              class="absolute bottom-4 left-1/2 flex -translate-x-1/2 cursor-pointer items-center gap-1.5 rounded-full bg-iris-600 px-4 py-2 text-xs font-medium text-white shadow-lg transition-colors hover:bg-iris-500 dark:bg-iris-500 dark:hover:bg-iris-400">
              <Icon icon="heroicons:arrow-down" class="size-3.5" />
              <span>Jump to latest</span>
            </button>
          </div>

          <div
            class="border-t border-charcoal-100 bg-white px-4 py-4 dark:border-charcoal-800 dark:bg-charcoal-900 sm:px-6">
            <div class="mx-auto max-w-4xl">
              <div class="relative">
                <!-- Sibling of the (overflow-hidden, for the textarea's rounded corners) input
                     box below, not a child of it — an absolutely-positioned popover inside an
                     overflow-hidden ancestor gets clipped instead of floating above it. -->
                <MentionMenu v-if="mention.isOpen()" :results="mention.results.value" :loading="mention.loading.value"
                  :hint="mention.hint.value" v-model:active-index="mention.activeIndex.value"
                  @select="applyMentionSelection" />
                <div
                  class="relative flex items-end overflow-hidden rounded-2xl border border-charcoal-200 bg-white shadow-sm transition-all focus-within:border-iris-400 focus-within:ring-2 focus-within:ring-iris-400 dark:border-charcoal-700 dark:bg-charcoal-850 dark:focus-within:border-iris-500 dark:focus-within:ring-iris-500">
                  <textarea v-model="draft" ref="mainInput" autofocus rows="1"
                    placeholder="Type a message or command..."
                    class="agent-chat-scrollbar max-h-52 w-full resize-none bg-transparent px-5 py-4 pr-28 text-[15px] leading-relaxed text-charcoal-900 outline-none placeholder-charcoal-400 disabled:cursor-not-allowed disabled:opacity-60 dark:text-charcoal-100 dark:placeholder-charcoal-500"
                    :disabled="streaming" @input="handleTextareaInput" @keydown="handleTextareaKeydown" />
                  <button v-if="streaming"
                    class="absolute bottom-2.5 right-2.5 flex cursor-pointer items-center justify-center gap-1.5 rounded-xl bg-charcoal-800 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-charcoal-700 dark:bg-charcoal-700 dark:hover:bg-charcoal-600"
                    @click="stopStreaming">
                    <Icon icon="heroicons:stop-solid" class="size-4" />
                    <span>Stop</span>
                  </button>
                  <button v-else
                    class="absolute bottom-2.5 right-2.5 flex cursor-pointer items-center justify-center rounded-xl bg-iris-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-iris-500 disabled:cursor-not-allowed disabled:bg-charcoal-200 disabled:text-charcoal-400 dark:disabled:bg-charcoal-700 dark:disabled:text-charcoal-500"
                    :disabled="!draft.trim()" @click="send">
                    Send
                  </button>
                </div>
              </div>
              <div class="mt-2 text-center text-xs text-charcoal-400 dark:text-charcoal-500">AI can make mistakes.
                Consider
                verifying important information.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Dialog>
</template>

<script setup>
import { computed, ref, nextTick, watch, onMounted, onBeforeUnmount } from 'vue'
import Dialog from 'primevue/dialog'
import { Icon } from '@iconify/vue'
import { buildTaskAnswer, buildFieldLensRequestAnswer } from '@/components/Agent/toolPrompts.js'
import Conversation from '@/components/Agent/Conversation.vue'
import MentionMenu from '@/components/Agent/MentionMenu.vue'
import ToolsPopover from '@/components/Agent/ToolsPopover.vue'
import useAgentConversations, { normalizeAnswer } from '@/composables/useAgentConversations.js'
import useMentionAutocomplete from '@/composables/useMentionAutocomplete.js'

const isOpen = defineModel({ default: false })

const props = defineProps({
  initialMessage: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['consumed'])

const draft = ref('')
const scroller = ref(null)
const mainInput = ref(null)
const toolsPopover = ref(null)

function openToolsPopover(event) {
  toolsPopover.value?.toggle(event)
}

// Auto-scroll only follows streaming output while the user is near the bottom;
// once they scroll up, it stops until they scroll back down or use the pill.
// Direction (not just distance) is tracked so a scroll-up disengages instantly,
// rather than waiting to cross NEAR_BOTTOM_THRESHOLD_PX (easy to never cross
// when the next delta keeps forcing scrollTop back to the true bottom first).
const isNearBottom = ref(true)
const NEAR_BOTTOM_THRESHOLD_PX = 120
// Filters out tiny incidental scrollTop shifts (e.g. a border/focus ring nudging
// layout by a pixel or two when the textarea gets focus) so only a deliberate
// scroll-up gesture disengages auto-scroll.
const SCROLL_UP_TOLERANCE_PX = 12
const MAX_TEXTAREA_HEIGHT_PX = 208
let lastScrollTop = 0

// System prompt for the active conversation. Field Lens seeds this via initialMessage;
// otherwise conversations default to the generic assistant prompt.
const activeSystemPrompt = ref('generic')
const activeSystemPromptArgs = ref(undefined)

// Empty-state quick-start chips (a la ChatGPT/Claude/Perplexity's "new chat"
// suggestions): a one-click way into a conversation instead of a blank textarea,
// tailored per entry point so it reads as written for that context rather than
// generic filler.
//
// Each prompt is one of three shapes:
// - `message`: fully self-contained (or a legitimate one-question guided flow the
//   tools already expect) — sent immediately on click.
// - `template`: needs details only the user has (account, reason, etc.). Rather
//   than send something vague and let the model spend a turn on ask_user for
//   each blank, this drops a fill-in-the-blank draft into the input — the same
//   shorthand Gmail/Slack canned-response templates use — with the first
//   [placeholder] (or a bare "@" for a Salesforce record) pre-selected so typing
//   immediately overwrites it.
// - `tool`: the name of the matching agents/tools/*.js file — reuses that tool's
//   own slash command (see buildSlashCommands' `tool` field in
//   useMentionAutocomplete.js, dispatched here via runToolCommand) instead of
//   duplicating its run/template behavior. Use this when the chip's real job is
//   opening the same structured bubble/composer the slash command does (e.g. a
//   form with several fields) rather than a single plain-text blank.
//
// Generic prompts only map to things the tools can actually back up — there's no
// tool that looks up "my" Opportunities/POCs/check-ins by ownership
// (find_account/find_opportunity only search by name, and nothing touches
// Checkin at all) — so chips don't imply that kind of recall.
//
// `icon`/`accent` are purely cosmetic (Conversation.vue's promptIcon/
// promptAccentClasses fall back to a generic one per shape if omitted) — kept
// in sync with each chip's underlying tool and ToolsPopover.vue's own
// prefix-based category coloring (find → sky, search → iris, create → mantis,
// boost → peach, check/get/summarize → amber) so the same action reads as the
// same color everywhere in the chat.
const SUGGESTED_PROMPTS = {
  generic: [
    { label: 'Prep for a customer meeting', icon: 'heroicons:calendar-days', accent: 'amber', template: 'Check if @ has any open support cases before I meet with them.' },
    { label: 'Find similar wins', icon: 'heroicons:trophy', accent: 'sky', template: 'Find similar recent wins related to [workload or competitor].' },
    { label: 'Check product compatibility', icon: 'heroicons:shield-check', accent: 'amber', tool: 'check_compatibility_matrix' },
    { label: 'Review Field Lens requests', icon: 'heroicons:magnifying-glass', accent: 'iris', tool: 'search_field_lens_table' },
    { label: 'Create follow-up actions', icon: 'heroicons:clipboard-document-check', accent: 'mantis', tool: 'create_task' },
    { label: 'Find an internal guide', icon: 'heroicons:book-open', accent: 'iris', template: 'Search our internal docs for [what you are looking for].' },
  ],
  'field-lens': [
    { label: 'Check third-party validation', icon: 'heroicons:shield-check', accent: 'amber', template: 'Check if [product] version [version] is validated on [Nutanix product, e.g. AHV].' },
    { label: 'Check feature support', icon: 'heroicons:puzzle-piece', accent: 'iris', template: 'Check if Nutanix supports [capability I need].' },
    { label: 'My open requests', icon: 'heroicons:list-bullet', accent: 'iris', message: 'What is the status of my open Field Lens requests?' },
    { label: 'Boost a request', icon: 'heroicons:rocket-launch', accent: 'peach', template: 'Boost the Field Lens request for [account] because [reason].' },
  ],
}

const suggestedPrompts = computed(() => SUGGESTED_PROMPTS[activeSystemPrompt.value] ?? SUGGESTED_PROMPTS.generic)

const {
  activeConversationId,
  activeTool,
  conversations,
  deleteConversation,
  editUserMessage,
  historyError,
  historyLoading,
  isDeletingConversation,
  loadConversations,
  messages,
  onEvent,
  openBoostPicker,
  openToolPrompt,
  pendingBoost,
  pendingQuestion,
  pendingToolPrompt,
  regenerateMessage,
  retryLastMessage,
  saveToHistory,
  selectConversation,
  sendMessage,
  startNewConversation,
  stopStreaming,
  streamBuffer,
  streaming,
} = useAgentConversations()

// /task and /request each open a real, standalone form component (ModalTask.vue,
// FieldLensRequestWizard.vue — rendered inline in Conversation.vue) instead of the
// generic ToolFormPrompt; a plain truthy sentinel is enough to drive that since
// each component owns all of its own form state. Submitting never calls the
// backend directly — it hands the resolved fields to the model to call the real
// tool, same "structured input, model still calls the tool" contract every other
// slash command uses. The only difference between /task and /request is which
// answer builder turns the submitted payload into that instruction, so share
// the open/close/submit plumbing.
function createPendingComposer(buildAnswer) {
  const pending = ref(null)

  function open() {
    pending.value = {}
  }

  function close() {
    pending.value = null
  }

  async function submit(payload) {
    close()
    await handleAnswer(buildAnswer(payload))
  }

  return { pending, open, close, submit }
}

const {
  pending: pendingTask,
  open: openTaskComposer,
  close: closeTaskComposer,
  submit: handleTaskSubmit,
} = createPendingComposer(buildTaskAnswer)

const {
  pending: pendingFieldLensRequest,
  open: openFieldLensRequestComposer,
  close: closeFieldLensRequestComposer,
  submit: handleFieldLensRequestSubmit,
} = createPendingComposer(buildFieldLensRequestAnswer)

const mention = useMentionAutocomplete({
  openBoostPicker,
  openToolPrompt,
  openTaskComposer,
  openFieldLensRequestComposer,
})

// Every real action tool has exactly one matching slash command (see the `tool`
// field on each entry in buildSlashCommands, useMentionAutocomplete.js) — this
// is just that same lookup, used to find it by tool name instead of by typed
// "/command" text.
const { slashCommands } = mention

// Records what an @mention resolved to so send() can splice the Salesforce ID
// into the text sent to the model, while the chat bubble keeps showing the
// plain "@Name" the user actually typed. Cleared whenever the draft is (send,
// new conversation, switching conversations) since stale entries are harmless
// but pointless to keep around.
const mentions = ref([])

const sortedConversations = computed(() => {
  return [...conversations.value].sort((a, b) => {
    const aTime = new Date(a.updatedAt ?? a.createdAt ?? 0).getTime()
    const bTime = new Date(b.updatedAt ?? b.createdAt ?? 0).getTime()
    return bTime - aTime
  })
})

function isConversationStreaming(conversation) {
  return streaming.value && activeConversationId.value === conversation.id
}

// The server summarizes the first message into `title` once the reply finishes
// (see conversations.create); until then — or if that call failed — fall back
// to truncating the raw message so the sidebar entry never sits blank.
function conversationTitle(conversation) {
  if (conversation.title) return conversation.title

  const firstUserMessage = conversation.messages?.find((message) => message.role === 'user')
  return (firstUserMessage?.content ?? 'New Conversation').slice(0, 48)
}

function formatConversationTime(conversation) {
  const value = conversation.updatedAt ?? conversation.createdAt
  if (!value) return 'Recent'

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'Recent'

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
}

function handleScroll() {
  const el = scroller.value
  if (!el) return

  const scrolledUp = el.scrollTop < lastScrollTop - SCROLL_UP_TOLERANCE_PX
  lastScrollTop = el.scrollTop

  if (scrolledUp) {
    isNearBottom.value = false
    return
  }

  const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
  isNearBottom.value = distanceFromBottom <= NEAR_BOTTOM_THRESHOLD_PX
}

// `force` is for user-initiated actions (sending a message, opening the dialog,
// switching conversations) that should always land at the bottom; everything else
// (streaming deltas, tool-call updates) only follows if already near the bottom.
// isNearBottom flips synchronously here (not after the scroll below) so a stale
// `false` from before the dialog was reopened can't flash the pill for a tick.
async function scrollBottom(force = false) {
  if (force) isNearBottom.value = true
  else if (!isNearBottom.value) return

  await nextTick()
  if (!scroller.value) return

  scroller.value.scrollTop = scroller.value.scrollHeight
  lastScrollTop = scroller.value.scrollTop
}

// Grows the textarea to fit its content up to MAX_TEXTAREA_HEIGHT_PX, beyond which
// it scrolls internally instead of pushing the rest of the dialog around.
function autoGrowTextarea() {
  const el = mainInput.value
  if (!el) return

  el.style.height = 'auto'
  const contentFits = el.scrollHeight <= MAX_TEXTAREA_HEIGHT_PX
  el.style.height = `${Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT_PX)}px`
  // Textareas default to overflow-y: auto, so a scrollbar can appear even when
  // the content fits (browsers round scrollHeight, and that rounding can leave
  // it a hair short of the "true" content height) — only allow scrolling once
  // we've actually capped the height at MAX_TEXTAREA_HEIGHT_PX.
  el.style.overflowY = contentFits ? 'hidden' : 'auto'
}

// Fires on every keystroke: keeps the textarea's height in sync and lets the
// mention/command popover track whether the caret currently sits inside an
// open @mention or /command token.
function handleTextareaInput(event) {
  autoGrowTextarea()
  mention.handleInput(event.target.value, event.target.selectionStart ?? event.target.value.length)
}

// A single keydown handler covers both cases so Enter never double-fires: while
// the popover is open it drives keyboard navigation instead of sending, exactly
// like Slack/Linear/Notion's @mention menus.
function handleTextareaKeydown(event) {
  if (mention.handleKeydown(event, applyMentionSelection)) {
    event.preventDefault()
    return
  }

  // Mirrors the previous @keydown.enter.exact.prevent="send": plain Enter sends,
  // Enter with any system modifier (Shift for a newline, in particular) doesn't.
  if (event.key === 'Enter' && !event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey) {
    event.preventDefault()
    send()
  }
}

// Drops a template into the draft and pre-selects its one blank. Shared by a
// typed "/command" template, a suggested-prompt chip's own `template`, and
// (via dispatchCommand below) a `tool` chip/Capabilities click that resolves
// to a template command — one place doing this instead of three.
function applyTemplate(template) {
  draft.value = template
  nextTick(focusAndSelectPlaceholder)
}

// Shared dispatch for anything shaped like a buildSlashCommands entry (see
// useMentionAutocomplete.js) — a typed "/command", a suggested-prompt chip's
// `tool` field (runToolCommand below), or a Capabilities popover click
// (handleCapabilitySelect below): drop its `template` into the draft, or run
// its own bubble/composer and bring it into view.
function dispatchCommand(command) {
  // A `template` command is just one piece of free text — drop a fill-in-the-blank
  // draft into the input instead of opening a form, same shorthand as the
  // empty-state suggested-prompt chips (see handleSuggestedPrompt below).
  if (command.template) {
    applyTemplate(command.template)
    return
  }

  // Everything else opens a real bubble below the input — a structured form, the
  // Salesforce picker, or a full composer — so scroll down to bring it into view
  // instead of leaving the user looking at wherever they already were.
  command.run()
  scrollBottom(true)
  nextTick(() => mainInput.value?.focus())
}

// Splices the selected result into the draft (mention) or runs it immediately
// (slash command). Keeps the actual text mutation here, where `draft` and
// `mainInput` live, so the composable stays UI-agnostic.
function applyMentionSelection(result) {
  const activeTrigger = mention.trigger.value
  if (!activeTrigger) return

  if (activeTrigger.type === 'command') {
    draft.value = draft.value.slice(0, activeTrigger.start) + draft.value.slice(activeTrigger.end)
    mention.close()
    dispatchCommand(result)
    return
  }

  const insertText = `@${result.name} `
  const before = draft.value.slice(0, activeTrigger.start)
  const after = draft.value.slice(activeTrigger.end)

  draft.value = `${before}${insertText}${after}`
  mentions.value = [...mentions.value, {
    display: `@${result.name}`,
    recordType: result.recordType,
    id: result.id,
    // Only populated for Contact results (see searchMentionCandidates) — carried
    // through so resolveMentions can give the model more than a name + ID.
    email: result.email,
    phone: result.phone,
    title: result.title,
  }]
  mention.close()

  nextTick(() => {
    const caret = before.length + insertText.length
    mainInput.value?.focus()
    mainInput.value?.setSelectionRange(caret, caret)
  })
}

// Shared by the Capabilities popover and any suggested-prompt chip whose `tool`
// field points at a slash command instead of spelling out its own template —
// runs the exact same run/template dispatch applyMentionSelection uses for a
// typed "/command", minus the text-splicing (nothing was typed here).
function runToolCommand(toolName) {
  const command = slashCommands.find((entry) => entry.tool === toolName)
  if (!command) return

  dispatchCommand(command)
}

// Capabilities popover (ToolsPopover.vue) entries are backend tool metadata, not
// slash commands themselves — this maps a clicked capability back to its command
// by name.
function handleCapabilitySelect(toolItem) {
  runToolCommand(toolItem.name)
}

// Splices each mention's resolved Salesforce ID (plus, for Contacts, whatever
// extra details we already have on hand — email/phone/title) inline into the
// outgoing text so the model gets it directly instead of calling
// find_account/find_opportunity/find_contact and guessing from the name alone.
// The chat bubble (the `display` option on sendMessage) is left showing
// exactly what the user typed.
function resolveMentions(text, mentionList) {
  return mentionList.reduce((resolved, item) => {
    if (!resolved.includes(item.display)) return resolved

    const details = [`Salesforce ${item.recordType} ID: ${item.id}`]
    if (item.title) details.push(`Title: ${item.title}`)
    if (item.email) details.push(`Email: ${item.email}`)
    if (item.phone) details.push(`Phone: ${item.phone}`)

    return resolved.replaceAll(item.display, `${item.display} (${details.join(', ')})`)
  }, text)
}

// Covers programmatic resets (send, switching conversations) in addition to the
// @input listener, which only fires for direct user typing.
watch(draft, () => {
  nextTick(autoGrowTextarea)
})

// Shared tail end of every send-like action: kick the request off first (the
// caller passes an already-started promise), then scroll immediately instead of
// waiting for it to finish, so streaming output the scroll listener picks up
// afterwards is following an auto-scroll that's already engaged.
async function sendAndScroll(sendPromise) {
  await scrollBottom(true)
  await sendPromise
}

// sendMessage only reads system_prompt/system_prompt_args on the conversation's
// first message, but every send-like action here might be that first message —
// mid-conversation ones just have it ignored — so they all route through this
// instead of repeating the two fields at each call site.
function sendWithContext(text, extra = {}) {
  return sendMessage(text, {
    system_prompt: activeSystemPrompt.value,
    system_prompt_args: activeSystemPromptArgs.value,
    ...extra
  })
}

async function send() {
  const text = draft.value.trim()
  if (!text || streaming.value) return

  const resolvedText = resolveMentions(text, mentions.value)

  draft.value = ''
  mentions.value = []
  mention.close()

  await sendAndScroll(sendWithContext(resolvedText, { display: text }))
}

async function handleAnswer(answer) {
  const { text, display } = normalizeAnswer(answer)
  if (!text || streaming.value) return

  await sendAndScroll(sendWithContext(text, { display }))
}

// `tool` chips delegate to runToolCommand, same as a Capabilities click.
// Immediate-send chips go straight through handleAnswer; template chips instead
// park a fill-in-the-blank draft in the input — the draft watcher above already
// resizes the textarea — and just pre-select the first [placeholder] so the user
// supplies the missing details in one message instead of the model spending a
// turn on ask_user for each one.
async function handleSuggestedPrompt(prompt) {
  if (streaming.value) return

  if (prompt.tool) {
    runToolCommand(prompt.tool)
    return
  }

  if (prompt.template) {
    applyTemplate(prompt.template)
    return
  }

  await handleAnswer(prompt.message)
}

// Matches the first [placeholder] in a template so it can be pre-selected.
const TEMPLATE_PLACEHOLDER = /\[[^\]]+\]/

// Templates fill their one blank one of two ways: a "[placeholder]" the user is
// meant to type over (see SUGGESTED_PROMPTS/buildSlashCommands' `template`
// entries), or a bare "@" — for a command whose blank is a Salesforce record —
// left for the user to type a name after, the same as manually typing "@" ever
// would. There's nothing to select for the latter, just a caret dropped right
// after it so the very next keystroke opens the mention popover exactly like it
// does when a user types the "@" themselves.
function focusAndSelectPlaceholder() {
  const el = mainInput.value
  if (!el) return

  el.focus()

  const bracketMatch = TEMPLATE_PLACEHOLDER.exec(el.value)
  if (bracketMatch) {
    el.setSelectionRange(bracketMatch.index, bracketMatch.index + bracketMatch[0].length)
    return
  }

  const at = el.value.indexOf('@')
  const caret = at === -1 ? el.value.length : at + 1
  el.setSelectionRange(caret, caret)
}

async function handleRetry() {
  await sendAndScroll(retryLastMessage())
}

async function handleRegenerate(index) {
  await sendAndScroll(regenerateMessage(index))
}

async function handleEdit({ index, text }) {
  await sendAndScroll(editUserMessage(index, text))
}

// Shared tail of every "leave whatever's half-typed/half-open behind" action —
// switching/starting a conversation, or reopening the dialog fresh. Deliberately
// not reused by send() below, which resets the draft/mentions but leaves an open
// /task or /request composer in place.
function resetComposerState() {
  draft.value = ''
  mentions.value = []
  mention.close()
  closeTaskComposer()
  closeFieldLensRequestComposer()
}

async function handleSelectConversation(conversation) {
  const selected = await selectConversation(conversation)
  if (!selected) return

  activeSystemPrompt.value = 'generic'
  activeSystemPromptArgs.value = undefined
  resetComposerState()
  await scrollBottom(true)
}

async function handleStartNewConversation() {
  const started = startNewConversation()
  if (!started) return

  activeSystemPrompt.value = 'generic'
  activeSystemPromptArgs.value = undefined
  resetComposerState()
  isNearBottom.value = true
  await nextTick()
  mainInput.value?.focus()
}

// Entry point for the navbar button and the keyboard shortcut. A Field Lens chat is
// hidden from history, so reopening the assistant has to leave it behind rather than
// drop the user back into a chat with no sidebar.
function openAssistant() {
  if (!saveToHistory.value && startNewConversation()) {
    activeSystemPrompt.value = 'generic'
    activeSystemPromptArgs.value = undefined
    resetComposerState()
  }

  isOpen.value = true
}

defineExpose({ openAssistant })

async function focusMainInput() {
  await nextTick()
  requestAnimationFrame(() => {
    mainInput.value?.focus()
  })
}

function handleGlobalShortcut(event) {
  if (event.key.toLowerCase() !== 'k' || event.altKey || event.shiftKey) return
  if (!event.metaKey && !event.ctrlKey) return

  const target = event.target
  if (target instanceof HTMLElement) {
    const tagName = target.tagName
    if (target.isContentEditable || tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT') {
      return
    }
  }

  event.preventDefault()
  openAssistant()
}

onMounted(() => {
  window.addEventListener('keydown', handleGlobalShortcut)
})

const stopListeningToConversationEvents = onEvent(async (event) => {
  await scrollBottom()

  if (event.type === 'done') {
    await nextTick()
    mainInput.value?.focus()
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleGlobalShortcut)
  stopListeningToConversationEvents()
})

watch(isOpen, async (visible) => {
  if (!visible) return
  if (saveToHistory.value) await loadConversations()
  await nextTick()
  await scrollBottom(true)
})

// Start a new conversation seeded with a message handed in by another view
watch(() => props.initialMessage, async (payload) => {
  if (!payload?.message || streaming.value) return

  isOpen.value = true

  const keepInHistory = payload.save_to_history !== false

  if (keepInHistory) await loadConversations()

  const started = startNewConversation({ saveToHistory: keepInHistory })
  if (!started) return

  emit('consumed')

  activeSystemPrompt.value = payload.system_prompt ?? 'generic'
  activeSystemPromptArgs.value = payload.system_prompt_args

  await sendAndScroll(sendWithContext(payload.message))
})
</script>

<style>
/* Keyframes Tailwind can't generate via arbitrary values */
.agent-chat-scrollbar::-webkit-scrollbar {
  width: 8px;
}

.agent-chat-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.agent-chat-scrollbar::-webkit-scrollbar-thumb {
  background: #d7d7d7;
  border-radius: 9999px;
}

.dark .agent-chat-scrollbar::-webkit-scrollbar-thumb {
  background: #444444;
}

.agent-chat-dialog {
  width: 80vw !important;
  max-width: 80vw !important;
  height: 80vh;
  max-height: 80vh !important;
  display: flex;
  flex-direction: column;
}

.agent-chat-dialog .p-dialog-header {
  flex: 0 0 auto;
}

.agent-chat-dialog .p-dialog-content {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden !important;
  display: flex;
  flex-direction: column;
}
</style>
